import { useState } from "react";
import { ArrowUpRight, BarChart3, PackageCheck, Printer, Search, ShoppingCart, Sparkles, Tag, TrendingUp } from "lucide-react";
import { Link, useNavigate } from "react-router-dom";
import { SummaryCard } from "../Components/admin-shell/dashboardUi";
import { getRoleLabel } from "../utils/roles";

const TAB_KEY = "adminDashboardTab";

const ORDER_CARDS = [
  { key: "newOrders", title: "الطلبات الجديدة", path: "/new-orders", color: "from-blue-600 to-cyan-500", highlight: true },
  { key: "confirmedOrders", title: "الطلبات المؤكدة", path: "/confirmed-orders", color: "from-emerald-600 to-green-500" },
  { key: "canceledOrders", title: "الطلبات الملغاة", path: "/canceled-orders", color: "from-red-600 to-rose-500" },
  { key: "holdOrders", title: "الطلبات المعلقة", path: "/hold-orders", color: "from-amber-500 to-yellow-500", highlight: true },
  { key: "changesOrders", title: "تحت التغييرات", path: "/changes-orders", color: "from-purple-600 to-violet-500" },
  { key: "customOrders", title: "التصاميم المخصصة", path: "/custom-orders", color: "from-teal-600 to-emerald-500" },
  { key: "depositOrders", title: "تتطلب دفعة أولية", path: "/deposit-required-orders", color: "from-orange-500 to-amber-500", highlight: true },
];

const PRODUCTION_CARDS = [
  { key: "printed", title: "Printed", path: "/printed-orders", color: "from-teal-600 to-emerald-500", showPieces: true },
  { key: "girabatcom", title: "Girabatcom", path: "/girabatcom-orders", color: "from-cyan-600 to-blue-500", showPieces: true },
  { key: "printingHold", title: "Printing Hold", path: "/printing-hold-orders", color: "from-slate-700 to-cyan-500", showPieces: true },
  { key: "incomplete", title: "Incomplete", path: "/incomplete-orders", color: "from-amber-600 to-orange-500", showPieces: true },
  { key: "printlet", title: "طلبات Printlet", path: "/printlet-orders", color: "from-fuchsia-600 to-pink-500" },
  { key: "teePlus", title: "Tee Plus", path: "/teeplus-orders", color: "from-emerald-600 to-teal-500" },
];

function readStoredTab() {
  try {
    const saved = sessionStorage.getItem(TAB_KEY);
    if (saved === "printing" || saved === "production") return "production";
    if (saved === "orders") return "orders";
  } catch (_) {}
  return "orders";
}

function Shortcut({ to, icon: Icon, title, description, className }) {
  return (
    <Link
      to={to}
      className={`group flex items-center gap-3 rounded-2xl border border-white/20 px-4 py-3.5 text-right text-white shadow-lg transition hover:-translate-y-0.5 hover:shadow-xl ${className}`}
    >
      <span className="rounded-xl bg-white/15 p-2.5">
        <Icon size={20} />
      </span>
      <span className="min-w-0">
        <span className="block text-base font-bold">{title}</span>
        <span className="mt-0.5 block text-sm text-white/80">{description}</span>
      </span>
    </Link>
  );
}

const SERVICE_CARDS = [
  { key: "tickets", title: "التذاكر", path: "/tickets", color: "from-rose-600 to-pink-500", unit: "تذكرة", highlight: true },
  { key: "replacements", title: "طلبات الاستبدال", path: "/replacement-orders", color: "from-violet-600 to-indigo-500", highlight: true },
  { key: "returns", title: "المرتجعات", path: "/returns", color: "from-sky-600 to-cyan-500", unit: "قطعة", productionOnly: true },
];

function ServiceCards({ summaries, access, navigate, includeReturns = false }) {
  const cards = SERVICE_CARDS.filter((card) => {
    if (card.productionOnly && !includeReturns) return false;
    return Boolean(access[card.key]);
  });
  if (cards.length === 0) return null;

  return (
    <section className="space-y-3">
      <h2 className="text-lg font-bold text-slate-800">متابعة</h2>
      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        {cards.map((card) => {
          const data = summaries[card.key] || { count: 0, loading: false };
          return (
            <SummaryCard
              key={card.key}
              item={{
                title: card.title,
                color: card.color,
                unit: card.unit,
                highlight: card.highlight,
                loading: data.loading,
                data: { count: data.count },
              }}
              onClick={() => navigate(card.path)}
              options={{ showAmount: false }}
            />
          );
        })}
      </div>
    </section>
  );
}

function Shortcuts({ access }) {
  const items = [
    access.productionShortcut && {
      to: "/production",
      icon: Printer,
      title: "لوحة الإنتاج",
      description: "طابور الطباعة — ٦×٢ ماكينة",
      className: "bg-gradient-to-br from-indigo-600 to-blue-600",
    },
    access.orders && {
      to: "/search-orders",
      icon: Search,
      title: "البحث عن طلب",
      description: "ابحث باستخدام رقم الطلب أو بيانات العميل",
      className: "bg-gradient-to-br from-slate-700 to-slate-900",
    },
    access.analyticsShortcut && {
      to: "/analytics",
      icon: BarChart3,
      title: "لوحة التحليلات",
      description: "المبيعات وأداء المتجر",
      className: "bg-gradient-to-br from-amber-500 to-orange-600",
    },
    access.designCategoriesShortcut && {
      to: "/categories",
      icon: Tag,
      title: "فئات التصاميم",
      description: "ترتيب وعرض فئات المتجر",
      className: "bg-gradient-to-br from-teal-600 to-emerald-600",
    },
  ].filter(Boolean);

  if (items.length === 0) return null;

  return (
    <section className="space-y-3">
      <h2 className="text-lg font-bold text-slate-800">اختصارات</h2>
      <div className="grid gap-3 md:grid-cols-2">
        {items.map((shortcut) => (
          <Shortcut key={shortcut.to} {...shortcut} />
        ))}
      </div>
    </section>
  );
}

export default function DashboardOverview({ role, summaries }) {
  const navigate = useNavigate();
  const access = summaries.access;
  const [tab, setTab] = useState(readStoredTab);

  const selectTab = (next) => {
    setTab(next);
    try { sessionStorage.setItem(TAB_KEY, next); } catch (_) {}
  };

  return (
    <div className="space-y-5 md:space-y-6">
      <section className="rounded-2xl border border-slate-200 bg-white px-4 py-4 md:px-5">
        <p className="text-xs font-semibold text-indigo-600">Girabatcom</p>
        <h1 className="mt-1 text-2xl font-extrabold text-slate-900">مركز التحكم</h1>
        <p className="mt-1 text-sm text-slate-500">
          مرحباً {getRoleLabel(role)}. اختر صفحة من القائمة للوصول إلى الأدوات المتاحة لحسابك.
        </p>
      </section>

      {access.orders && (
        <div className="grid grid-cols-2 gap-1 rounded-2xl border border-slate-200 bg-white p-1 shadow-sm">
          <button
            type="button"
            onClick={() => selectTab("orders")}
            className={`inline-flex items-center justify-center gap-2 rounded-xl px-3 py-3 text-sm font-bold transition md:text-lg ${
              tab === "orders"
                ? "bg-gradient-to-br from-indigo-600 to-purple-600 text-white shadow-md shadow-indigo-500/30"
                : "text-slate-500 hover:bg-slate-50 hover:text-slate-800"
            }`}
          >
            <ShoppingCart size={18} />
            الطلبات
          </button>
          <button
            type="button"
            onClick={() => selectTab("production")}
            className={`inline-flex items-center justify-center gap-2 rounded-xl px-3 py-3 text-sm font-bold transition md:text-lg ${
              tab === "production"
                ? "bg-gradient-to-br from-fuchsia-600 to-pink-600 text-white shadow-md shadow-fuchsia-500/30"
                : "text-slate-500 hover:bg-slate-50 hover:text-slate-800"
            }`}
          >
            <PackageCheck size={18} />
            الإنتاج
          </button>
        </div>
      )}

      {tab === "orders" && (
        <>
          {access.hero && (
            <section className="relative overflow-hidden rounded-2xl border border-white/20 bg-gradient-to-br from-indigo-600 via-purple-600 to-pink-600 px-4 py-3.5 shadow-lg shadow-indigo-500/20 md:px-6 md:py-4">
              <div className="relative flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
                <div className="flex items-center gap-2.5">
                  <div className="rounded-xl border border-white/20 bg-white/15 p-2 text-white">
                    <Sparkles size={16} />
                  </div>
                  <div>
                    <h2 className="text-lg font-extrabold leading-tight text-white md:text-xl">إجمالي الطلبات الحالية</h2>
                    <p className="text-xs text-white/70">المؤكدة + التغييرات + الدفعات + المخصصة</p>
                  </div>
                </div>
                {summaries.heroLoading ? (
                  <div className={`grid w-full gap-3 md:w-auto md:min-w-[320px] ${access.amounts ? "grid-cols-2" : "grid-cols-1"}`}>
                    <div className="h-14 animate-pulse rounded-xl bg-white/15" />
                    {access.amounts && <div className="h-14 animate-pulse rounded-xl bg-white/15" />}
                  </div>
                ) : (
                  <div className={`grid w-full gap-3 md:w-auto md:min-w-[320px] ${access.amounts ? "grid-cols-2" : "grid-cols-1"}`}>
                    <div className="rounded-xl border border-white/20 bg-white/15 px-3 py-2">
                      <div className="mb-0.5 flex items-center gap-1.5 text-[11px] text-white/70">
                        <ShoppingCart size={12} />
                        عدد الطلبات
                      </div>
                      <p className="text-xl font-extrabold leading-none text-white md:text-2xl">
                        {summaries.totalOrders.toLocaleString()}
                      </p>
                    </div>
                    {access.amounts && (
                      <div className="rounded-xl border border-white/20 bg-white/15 px-3 py-2">
                        <div className="mb-0.5 flex items-center gap-1.5 text-[11px] text-white/70">
                          <TrendingUp size={12} />
                          إجمالي المبلغ
                        </div>
                        <p className="text-xl font-extrabold leading-none text-white md:text-2xl">
                          {summaries.totalAmount.toLocaleString()}
                        </p>
                        <p className="mt-0.5 text-[10px] text-white/60">جنيه مصري</p>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </section>
          )}

          {access.orders && (
            <section className="space-y-3">
              <h2 className="text-lg font-bold text-slate-800">ملخص الطلبات</h2>
              <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
                {ORDER_CARDS.map((card) => {
                  const data = summaries.cards[card.key];
                  return (
                    <SummaryCard
                      key={card.key}
                      item={{
                        title: card.title,
                        color: card.color,
                        highlight: card.highlight,
                        loading: data.loading,
                        data: { count: data.count, totalAmount: data.amount },
                      }}
                      onClick={() => navigate(card.path)}
                      options={{ showAmount: access.amounts, compact: !access.hero }}
                    />
                  );
                })}
              </div>
            </section>
          )}

          <ServiceCards summaries={summaries} access={access} navigate={navigate} />

          <Shortcuts access={access} />
        </>
      )}

      {tab === "production" && access.orders && (
        <>
          <section className="space-y-3">
            <h2 className="text-lg font-bold text-slate-800">حالات الإنتاج</h2>
            <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
              {PRODUCTION_CARDS.map((card) => {
                const data = summaries.production?.[card.key] || { count: 0, amount: 0, pieces: 0, loading: false };
                return (
                  <SummaryCard
                    key={card.key}
                    item={{
                      title: card.title,
                      color: card.color,
                      loading: data.loading,
                      showPieces: card.showPieces,
                      data: { count: data.count, totalAmount: data.amount, totalProductsSold: data.pieces },
                    }}
                    onClick={() => navigate(card.path)}
                    options={{ showAmount: access.amounts, showPieces: card.showPieces, compact: !access.hero }}
                  />
                );
              })}
              {(access.printletShortcut ?? access.productionShortcut) && (
                <button
                  type="button"
                  onClick={() => navigate("/printlet")}
                  className="group relative flex min-h-[108px] flex-col justify-between overflow-hidden rounded-xl border border-white/20 bg-gradient-to-br from-fuchsia-600 to-pink-600 p-3.5 text-right text-white shadow-md transition hover:scale-[1.02] hover:shadow-xl"
                >
                  <h3 className="text-base font-bold md:text-lg">لوحة Printlet</h3>
                  <p className="mt-2 text-sm text-pink-100">طباعة خارجية · تتبع · مالية</p>
                  <span className="mt-3 inline-flex items-center gap-1 text-sm font-semibold">
                    فتح اللوحة
                    <ArrowUpRight size={14} />
                  </span>
                </button>
              )}
            </div>
          </section>
          <ServiceCards summaries={summaries} access={access} navigate={navigate} includeReturns />
          <Shortcuts access={access} />
        </>
      )}

      <footer className="pb-2 pt-4 text-center">
        <p className="text-xs text-slate-400">{new Date().getFullYear()} © Girabatcom — لوحة تحكم</p>
      </footer>
    </div>
  );
}
